import React, { useRef, useState } from 'react';
import { Tabs } from '~/components/Tabs';
import { useOnClickOutside } from '~/helpers/helpers';
import classes from './RentRollConfirmModal.module.css';

export type PreviewUser = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  unit?: string;
  moveOutDate?: string | null;
};

export type PreviewSummary = {
  moveInsCount: number;
  transfersCount: number;
  usersToMoveOutCount: number;
  newTenantsCount: number;
  totalIncomingRows: number;
  activeUserCount: number;
  currentAdoptionRate: number;
  projectedAdoptionRate: number;
  adoptionRateDrop: number;
  requiresExtraConfirmation: boolean;
  anyRowMissingPhone: boolean;
  newTenants: PreviewUser[];
  moveIns: PreviewUser[];
  transfers: PreviewUser[];
  usersToMoveOut: PreviewUser[];
  // Absent on previews created before reactivation shipped.
  reactivationsCount?: number;
  reactivations?: {
    userUuid: string;
    email: string | null;
    rosterEmail?: string | null;
  }[];
};

export type PreviewSection = {
  buildingName: string | null;
  summary: PreviewSummary;
};

type SingleBuildingProps = {
  summary: PreviewSummary;
  previews?: undefined;
};

type MultiBuildingProps = {
  summary?: undefined;
  previews: { buildingName: string; summary: PreviewSummary }[];
};

type RentRollPreviewSummaryProps = (
  | SingleBuildingProps
  | MultiBuildingProps
) & {
  isVisible: boolean;
  isConfirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

type TopTab = 'currentUsers' | 'tenants';
type TenantSubTab = 'newTenants' | 'moveIns' | 'transfers';

type ResidentTableVariant = 'newTenants' | 'moveIns' | 'transfers' | 'moveOut';

const formatPhone = (phone?: string) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) return phone;
  return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
};

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString();
};

const ResidentTable = ({
  users,
  variant
}: {
  users: PreviewUser[];
  variant: ResidentTableVariant;
}) => {
  const isMoveOut = variant === 'moveOut';
  return (
    <div
      className={`${classes.table} ${isMoveOut ? classes.tableMoveOut : ''}`}
    >
      <div className={classes.tableHead}>
        <div>Unit</div>
        <div>Resident</div>
        <div>Contact</div>
        {isMoveOut && <div>Move-out date</div>}
        <div>Change</div>
      </div>
      {users.length === 0 && (
        <div className={classes.tableEmpty}>No residents in this category.</div>
      )}
      {users.map((u, i) => {
        const missingPhone = !u.phone;
        const phoneDisplay = missingPhone
          ? 'No phone number'
          : formatPhone(u.phone);
        return (
          <div
            key={`${u.email}-${i}`}
            className={`${classes.tableRow} ${
              missingPhone ? classes.tableRowMissingPhone : ''
            }`}
          >
            <div className={classes.unit}>{u.unit || '—'}</div>
            <div>
              <div className={classes.residentName}>
                {u.firstName} {u.lastName}
              </div>
            </div>
            <div className={classes.contact}>
              <div className={classes.email}>{u.email}</div>
              <div
                className={`${classes.phone} ${
                  missingPhone ? classes.phoneMissing : ''
                }`}
              >
                <svg
                  width='11'
                  height='11'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7 12.8 12.8 0 0 0 .7 2.8 2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4 12.8 12.8 0 0 0 2.8.7 2 2 0 0 1 1.7 2Z' />
                </svg>
                {phoneDisplay}
              </div>
            </div>
            {isMoveOut && (
              <div
                className={`${classes.moveOutDate} ${
                  formatDate(u.moveOutDate) ? '' : classes.moveOutDateEmpty
                }`}
              >
                {formatDate(u.moveOutDate) || '—'}
              </div>
            )}
            <div className={classes.badges}>
              {variant === 'newTenants' && (
                <>
                  <span className={`${classes.badge} ${classes.badgeNew}`}>
                    New
                  </span>
                  <span className={`${classes.badge} ${classes.badgeMoveIn}`}>
                    Move-in
                  </span>
                </>
              )}
              {variant === 'moveIns' && (
                <span className={`${classes.badge} ${classes.badgeMoveIn}`}>
                  Move-in
                </span>
              )}
              {variant === 'transfers' && (
                <span className={`${classes.badge} ${classes.badgeTransfer}`}>
                  Transfer
                </span>
              )}
              {variant === 'moveOut' && (
                <span className={`${classes.badge} ${classes.badgeMoveOut}`}>
                  Move-out
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const RentRollPreviewBody = ({
  summary
}: {
  summary: PreviewSummary;
}) => {
  const [topTab, setTopTab] = useState<TopTab>('currentUsers');
  const [tenantSubTab, setTenantSubTab] = useState<TenantSubTab>('newTenants');

  const handleTopTabChange = (value: string) => {
    if (value === 'currentUsers' || value === 'tenants') setTopTab(value);
  };
  const handleTenantSubTabChange = (value: string) => {
    if (
      value === 'newTenants' ||
      value === 'moveIns' ||
      value === 'transfers'
    ) {
      setTenantSubTab(value);
    }
  };

  const visibleUsers: PreviewUser[] =
    topTab === 'currentUsers' ? summary.usersToMoveOut : summary[tenantSubTab];
  const visibleMissingCount = visibleUsers.filter((u) => !u.phone).length;

  // Deactivation is an all-or-nothing gate per building (see applyTenantUpsert): if
  // any row in this building's upload is missing a phone number, none of these
  // move-outs are actually deactivated on confirm, regardless of usersToMoveOutCount.
  const effectiveMoveOutCount = summary.anyRowMissingPhone
    ? 0
    : summary.usersToMoveOutCount;

  // Previews created before reactivation shipped have neither field.
  const reactivations = summary.reactivations ?? [];
  const reactivationsCount = summary.reactivationsCount ?? reactivations.length;

  const chips = [
    {
      label: 'Incoming rows',
      value: summary.totalIncomingRows,
      color: '#211d52'
    },
    { label: 'New tenants', value: summary.newTenantsCount, color: '#157a55' },
    { label: 'Move-ins', value: summary.moveInsCount, color: '#4a41c9' },
    {
      label: 'Transfers',
      value: summary.transfersCount,
      color: summary.transfersCount > 0 ? '#8a5a00' : '#a3a2b3'
    },
    {
      label: 'To be deactivated',
      value: effectiveMoveOutCount,
      color: effectiveMoveOutCount > 0 ? '#b3261e' : '#a3a2b3'
    },
    {
      label: 'Reactivations',
      value: reactivationsCount,
      color: reactivationsCount > 0 ? '#157a55' : '#a3a2b3'
    }
  ];

  const activeUsersAfter = summary.activeUserCount - effectiveMoveOutCount;
  const dropPct =
    summary.activeUserCount > 0
      ? Math.round((effectiveMoveOutCount / summary.activeUserCount) * 100)
      : 0;

  return (
    <div>
      <div className={classes.outcomeCards}>
        <div className={classes.outcomeCard}>
          <div className={classes.outcomeCardTop}>
            <span className={classes.outcomeCardLabel}>Active users</span>
            <span
              className={`${classes.pill} ${
                effectiveMoveOutCount > 0
                  ? classes.pillWarning
                  : classes.pillSteady
              }`}
            >
              {dropPct}% drop
            </span>
          </div>
          <div className={classes.outcomeCardValues}>
            <span className={classes.outcomeCardValue}>
              {summary.activeUserCount}
            </span>
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#a8a7b8'
              strokeWidth='2.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M5 12h14M13 6l6 6-6 6' />
            </svg>
            <span className={classes.outcomeCardValue}>{activeUsersAfter}</span>
          </div>
          <div className={classes.outcomeCardDescription}>
            {effectiveMoveOutCount > 0
              ? `${effectiveMoveOutCount} of ${summary.activeUserCount} active users will be dropped.`
              : 'No active users will be dropped.'}
          </div>
        </div>

        <div
          className={`${classes.outcomeCard} ${
            summary.requiresExtraConfirmation ? classes.outcomeCardDanger : ''
          }`}
        >
          <div className={classes.outcomeCardTop}>
            <span className={classes.outcomeCardLabel}>Adoption rate</span>
            <span
              className={`${classes.pill} ${
                summary.requiresExtraConfirmation
                  ? classes.pillDanger
                  : summary.adoptionRateDrop > 0
                  ? classes.pillWarning
                  : classes.pillSteady
              }`}
            >
              {summary.adoptionRateDrop > 0
                ? `-${summary.adoptionRateDrop} pts`
                : 'Steady'}
            </span>
          </div>
          <div className={classes.outcomeCardValues}>
            <span className={classes.outcomeCardValue}>
              {summary.currentAdoptionRate}%
            </span>
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#a8a7b8'
              strokeWidth='2.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M5 12h14M13 6l6 6-6 6' />
            </svg>
            <span className={classes.outcomeCardValue}>
              {summary.projectedAdoptionRate}%
            </span>
          </div>
          <div className={classes.outcomeCardDescription}>
            {summary.adoptionRateDrop > 0
              ? `Adoption drops by ${summary.adoptionRateDrop} percentage points after import.`
              : 'Adoption is unchanged after import.'}
          </div>
        </div>
      </div>

      {summary.anyRowMissingPhone && summary.usersToMoveOutCount > 0 && (
        <div className={classes.infoBanner}>
          <svg
            className={classes.warningBannerIcon}
            width='17'
            height='17'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#3a5ba0'
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='12' cy='12' r='10' />
            <path d='M12 16v-4M12 8h.01' />
          </svg>
          <div className={classes.infoBannerText}>
            <strong>Some rows are missing a phone number.</strong> No residents
            will be deactivated for this upload. Re-upload with complete phone
            data to process move-outs.
          </div>
        </div>
      )}

      {summary.requiresExtraConfirmation && effectiveMoveOutCount > 0 && (
        <div className={classes.warningBanner}>
          <svg
            className={classes.warningBannerIcon}
            width='17'
            height='17'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#c78900'
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M12 9v4M12 17h.01' />
            <path d='M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z' />
          </svg>
          <div className={classes.warningBannerText}>
            <strong>
              This import will deactivate {effectiveMoveOutCount} resident
              {effectiveMoveOutCount === 1 ? '' : 's'}, dropping adoption by{' '}
              {summary.adoptionRateDrop} percentage points.
            </strong>{' '}
            Please review the affected residents carefully before confirming.
          </div>
        </div>
      )}

      <div className={classes.chips}>
        {chips.map((chip) => (
          <div className={classes.chip} key={chip.label}>
            <div className={classes.chipValue} style={{ color: chip.color }}>
              {chip.value}
            </div>
            <div className={classes.chipLabel}>{chip.label}</div>
          </div>
        ))}
      </div>

      <div className={classes.residentsHeader}>
        <div className={classes.residentsTitle}>
          Affected residents{' '}
          <span className={classes.residentsCount}>
            ({visibleUsers.length})
          </span>
        </div>
        {visibleMissingCount > 0 && (
          <div className={classes.missingPhonePill}>
            <svg
              width='13'
              height='13'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M12 9v4M12 17h.01' />
              <path d='M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z' />
            </svg>
            {visibleMissingCount} missing phone
          </div>
        )}
      </div>

      <div className={classes.topTabs}>
        <Tabs.Container value={topTab} onChange={handleTopTabChange}>
          <Tabs.Option
            value='currentUsers'
            selected={topTab === 'currentUsers'}
          >
            Current users
          </Tabs.Option>
          <Tabs.Option value='tenants' selected={topTab === 'tenants'}>
            Tenants
          </Tabs.Option>
        </Tabs.Container>
      </div>

      {topTab === 'tenants' && (
        <div className={classes.subTabs}>
          <Tabs.Container
            value={tenantSubTab}
            onChange={handleTenantSubTabChange}
          >
            <Tabs.Option
              value='newTenants'
              selected={tenantSubTab === 'newTenants'}
            >
              New tenants ({summary.newTenants.length})
            </Tabs.Option>
            <Tabs.Option value='moveIns' selected={tenantSubTab === 'moveIns'}>
              Move-ins ({summary.moveIns.length})
            </Tabs.Option>
            <Tabs.Option
              value='transfers'
              selected={tenantSubTab === 'transfers'}
            >
              Transfers ({summary.transfers.length})
            </Tabs.Option>
          </Tabs.Container>
        </div>
      )}

      {reactivationsCount > 0 && (
        <div className={classes.reactivationsNote}>
          <b>Reactivations ({reactivationsCount}):</b>{' '}
          {reactivations
            .map((r) => {
              const account = r.email || r.userUuid;
              // A phone match can carry a new address; show both so it is clear which
              // account is being restored.
              return r.rosterEmail && r.rosterEmail !== r.email
                ? `${account} (uploaded as ${r.rosterEmail})`
                : account;
            })
            .join(', ')}
          <div className={classes.reactivationsHint}>
            These residents were deactivated by an earlier upload and are on
            this roster again. Confirming restores their original accounts.
          </div>
        </div>
      )}

      {topTab === 'tenants' ? (
        <ResidentTable users={summary[tenantSubTab]} variant={tenantSubTab} />
      ) : (
        <ResidentTable users={summary.usersToMoveOut} variant='moveOut' />
      )}
    </div>
  );
};

const RentRollConfirmModal = (props: RentRollPreviewSummaryProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => props.onCancel());

  if (!props.isVisible) return null;

  const sections: PreviewSection[] = props.previews
    ? props.previews
    : [{ buildingName: null, summary: props.summary }];
  const isMultiBuilding = sections.length > 1;
  const totalIncomingRows = sections.reduce(
    (sum, section) => sum + section.summary.totalIncomingRows,
    0
  );
  // Deactivation is an all-or-nothing gate per building (see applyTenantUpsert):
  // if any row in that building's upload is missing a phone number, none of that
  // building's move-outs are actually deactivated, regardless of usersToMoveOutCount.
  const totalToBeDeactivated = sections.reduce(
    (sum, section) =>
      sum +
      (section.summary.anyRowMissingPhone
        ? 0
        : section.summary.usersToMoveOutCount),
    0
  );

  return (
    <div className={classes.overlay}>
      <div ref={ref} className={classes.modal}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.headerIcon}>
              <svg
                width='22'
                height='22'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M3 21h18' />
                <path d='M5 21V7l7-4 7 4v14' />
                <path d='M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01' />
              </svg>
            </div>
            <div>
              <div className={classes.headerTitle}>
                {isMultiBuilding
                  ? `Confirm Rent Roll Import (${sections.length} buildings)`
                  : 'Confirm Rent Roll Import'}
              </div>
              <div className={classes.headerSubtitle}>
                Review the changes below before applying them to your portfolio.
              </div>
            </div>
          </div>
          <button
            title='Cancel'
            className={classes.closeButton}
            onClick={() => props.onCancel()}
          >
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.2'
              strokeLinecap='round'
            >
              <path d='M18 6 6 18M6 6l12 12' />
            </svg>
          </button>
        </div>

        <div className={classes.scrollBody}>
          {sections.map((section, i) => (
            <div key={section.buildingName ?? i}>
              {section.buildingName && (
                <div
                  className={`${classes.buildingSectionTitle} ${
                    i > 0 ? classes.buildingSectionDivider : ''
                  }`}
                >
                  {section.buildingName}
                </div>
              )}
              <RentRollPreviewBody summary={section.summary} />
            </div>
          ))}
        </div>

        <div className={classes.footer}>
          <div className={classes.footerInfo}>
            <svg
              width='14'
              height='14'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <circle cx='12' cy='12' r='10' />
              <path d='M12 16v-4M12 8h.01' />
            </svg>
            Importing <strong>{totalIncomingRows} rows</strong>
            {totalToBeDeactivated > 0 ? (
              <>
                {' '}
                — <strong>{totalToBeDeactivated}</strong> existing user
                {totalToBeDeactivated === 1 ? '' : 's'} will be deactivated.
              </>
            ) : (
              <> — no existing users removed.</>
            )}
          </div>
          <div className={classes.footerButtons}>
            <button
              className={classes.cancelButton}
              onClick={() => props.onCancel()}
            >
              Cancel
            </button>
            <button
              className={classes.confirmButton}
              disabled={props.isConfirming}
              onClick={() => props.onConfirm()}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.4'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M20 6 9 17l-5-5' />
              </svg>
              {props.isConfirming ? 'Confirming...' : 'Confirm Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentRollConfirmModal;
